import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './idb-highcharts.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import * as Highcharts from 'highcharts';

interface CustomPointOptions {
  y: number;
  marker?: object;
  dataLabels?: object;
  color?: string;
}

interface CustomYAxis {
  min: number;
  max: number;
  tickInterval: number;
  allowDecimals: boolean;
}

/**
 * The IDB Highcharts component.
 * @element idb-highcharts
 * @extends OutlineElement
 */
@customElement('idb-highcharts')
export class IdbHighcharts extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: Array, attribute: 'chart-values' })
  chartValues = [];

  @property({ type: Array, attribute: 'chart-categories' })
  chartCategories = [];

  @property({ type: Object, attribute: 'chart-y-axis' })
  chartYAxis: CustomYAxis;

  @property({ type: Boolean, attribute: 'has-percent' })
  hasPercent = false;

  @property({ type: String, attribute: 'locale' })
  locale = 'en-US';

  

  private chart?: Highcharts.Chart;

  firstUpdated() {
    this.createChart();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('chartValues') && this.chart) {
      this.chart.update(this.createSeries());
    }
  }

  // Método para inicializar el gráfico
  private createChart() {
    const container = this.shadowRoot?.getElementById(
      'chart-container'
    ) as HTMLElement;

    if (container) {
      this.chart = Highcharts.chart(container, {
        ...this.defaultOptions(),
        ...this.createSeries(),
      });
    }
  }

  render(): TemplateResult {
    return html`<div id="chart-container"></div>`;
  }

  private createSeries(): Highcharts.Options {
    const showPercent = this.hasPercent;
    const locale = this.locale;
    if (this.chartValues) {
      const categories = this.chartCategories;

      const data: CustomPointOptions[] = this.chartValues.reduce(
        (total: CustomPointOptions[], current, index) => {
          const point: CustomPointOptions = { y: current };
          if (index == 0) {
            point.color = '#ffffff';
          }
          if (index == this.chartValues.length - 1) {
            point.color = '#ffffff';
            point.marker = {
              radius: 6,
              lineWidth: 2,
              lineColor: '#555555',
            };
            point.dataLabels = {
              enabled: true,
              style: {
                color: '#555555',
                fontWeight: 'bolder',
                fontSize: 15,
              },
            };
          }
          total.push(point);
          return total;
        },
        []
      );

      return {
        chart: {
          style: {
            fontFamily: 'var(--ff-display)',
          },
          backgroundColor: 'transparent',
        },
        xAxis: {
          gridLineWidth: 0,
          categories: categories,
          title: {
            text: '',
          },
        },
        plotOptions: {
          series: {
            lineWidth: 10,
            marker: {
              radius: 3,
            },
          },
        },
        series: [
          {
            type: 'line',
            name: '',
            keys: ['y', 'color'],
            color: '#1e9add',
            data: data,
            dataLabels: {
              enabled: true,
              style: {
                fontSize: 'var(--fs-xs)',
                color: '#888888',
                textOutline: '#ffffff',
              },
              formatter: function (this: Highcharts.Point) {
                const numericValue = Number(this.y);
                const formattedNumber = numericValue.toLocaleString(locale);
                return formattedNumber + (showPercent ? '%' : '');
              },
            },
            showInLegend: false,
          },
        ],
      };
    }
    return {};
  }

  private defaultOptions(): Highcharts.Options {

    return {
      chart: {
        backgroundColor: 'transparent',
        type: 'line',
      },
      title: {
        text: '',
      },
      exporting: {
        enabled: false,
      },
      yAxis: {
        gridLineWidth: 0,
        title: {
          text: '',
        },
        allowDecimals: this.chartYAxis?.allowDecimals,
        lineWidth: 1,
        lineColor: '#000',
        min: this.chartYAxis?.min,
        max: this.chartYAxis?.max,
        tickInterval: this.chartYAxis?.tickInterval,
      },
      plotOptions: {
        series: {
          lineWidth: 9,
        },
      },
      tooltip: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    };
  }
}
